// contexts/TimeModalContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { handleBackgroundScroll } from "../utils/backgroundScroll";
/**
* Target object describing the time edit details.
*/
export interface TimeEditTarget {
    /** Type of time edit: single entry or group */
    type: "single" | "group";
    /** Date string representing the target date */
    date: string;
    /** Time string representing the target time */
    time: string;
    /** Optional product ID related to the time edit */
    productId?: number;
    /** Optional product type or variant */
    prodType?: string;
    /** Optional make the date not switchable */
    static?: boolean;
    /** Optional max or min time */
    restrictions?: { min: string, max: string }
    /** Optional amount */
    amount?: number,
    /** Optional order in advance*/
    advance?: number
}

/**
* Shape of the TimeModal Context value.
*/
interface TimeModalContextType {
    /** Whether the time modal is currently open */
    isOpen: boolean;
    /** Function to open the time modal */
    open: () => void;
    /** Function to close the time modal and reset target */
    close: () => void;
    /** Sets the current time edit target or null to clear */
    setTimeTarget: (target: TimeEditTarget | null) => void;
    /** Current target data for time editing, or null if none */
    timeTarget: TimeEditTarget | null;
    /** Last selected time */
    lastTime: string;
    /** Setter for the last time */
    setLastTime: (val:string)=>void;
    /** Whether the timemodal is open over a differrent modal */
    openOnTop: boolean;
    /** Whether the timemodal is open over a differrent modal setter*/
    setOpenOnTop: (val: boolean) => void;
}

/**
* React context to manage state and actions for a time editing modal.
* Provides modal open state, open/close methods, and the current time edit target.
*/
const TimeModalContext = createContext<TimeModalContextType | undefined>(undefined);

/**
* Provider component that manages time modal state and provides context to children.
* It also locks background scroll while the modal is open.
* 
* @param children - React child elements
*/
export const TimeModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [ openOnTop, setOpenOnTop ] = useState<boolean>(false)
    const [lastTime, setLastTime] = useState<string>(() => {
        const saved = sessionStorage.getItem("lastSavedTime");
        return saved ? JSON.parse(saved) : "";
    });
    useEffect(() => {
        sessionStorage.setItem("lastSavedTime", JSON.stringify(lastTime));
    }, [lastTime]);

    const [isOpen, setIsOpen] = useState(false); // Modal open flag
    const [timeTarget, setTimeTarget] = useState<TimeEditTarget | null>(null);

    const open = () => setIsOpen(true); // Open time modal
    const close = () => { // Close time modal 
        setIsOpen(false);
        setTimeTarget(null);
    };
    // Stop scroll on open
    useEffect(()=>{
        // Handle only closing background scroll while open on top
        if(openOnTop && isOpen === false){
            setOpenOnTop(false);
            return;
        }
        handleBackgroundScroll(isOpen);
    }, [isOpen])



    return (
        <TimeModalContext.Provider value={{ isOpen, open, close, timeTarget, setTimeTarget, lastTime, setLastTime, openOnTop, setOpenOnTop}}>
        {children}
        </TimeModalContext.Provider>
    );
};
/**
* Hook to access the TimeModalContext.
* Throws an error if used outside of a TimeModalProvider.
* 
* @returns TimeModalContextType object with modal state and control functions
*/
export const useTimeModal = () => {
    const context = useContext(TimeModalContext);
    if (!context) throw new Error("useTimeModal must be used within a TimeModalProvider");
    return context;
};